import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Coins, Flame, Check, Target, BookOpen, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getMissionsToday } from "@/lib/api";
import { BackButton } from "@/components/BackButton";

function Bar({ current, target }) {
  const pct = Math.min(100, Math.round((current / target) * 100));
  return (
    <div className="h-1.5 w-full rounded-full bg-surfaceTertiary overflow-hidden">
      <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function MissionRow({ m }) {
  return (
    <div data-testid={`mission-${m.id}`} className="flex items-center gap-3 py-2.5">
      <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.done ? "bg-brand text-onBrand" : "bg-surfaceTertiary text-subtle"}`}>
        {m.done ? <Check size={16} /> : <Target size={15} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <p className={`text-sm font-medium ${m.done ? "text-onSurface" : "text-onSurfaceSecondary"}`}>{m.label}</p>
          <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-brand shrink-0"><Coins size={12} /> +{m.reward}</span>
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <Bar current={m.current} target={m.target} />
          <span className="text-[11px] font-mono text-subtle shrink-0">{m.current}/{m.target}</span>
        </div>
      </div>
    </div>
  );
}

export default function Rules() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { if (user) getMissionsToday().then(setData).catch(() => {}); }, [user]);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const missions = data?.missions || [];
  const allComplete = missions.find((m) => m.id === "all_complete");
  const daily = missions.filter((m) => m.id !== "all_complete");
  const streakTable = data?.streak_table || [];
  const currentStreak = data?.streak ?? user.streak ?? 0;

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-10" data-testid="missioni-page">
      <BackButton />
      <div className="flex items-center gap-2.5 mb-2">
        <BookOpen className="text-brand" size={26} />
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-onSurface">Missioni</h1>
      </div>
      <p className="text-sm text-onSurfaceSecondary mb-4">
        Leggi articoli per guadagnare Readoo. Ogni articolo valido (letto fino in fondo) vale <b>+10 Readoo</b> (di più salendo di livello).
        In più puoi completare le missioni giornaliere e mantenere la tua streak.
      </p>
      <div className="mb-8 rounded-xl border border-brand/30 bg-brandTertiary/30 px-4 py-3 flex items-start gap-2.5">
        <Bell size={16} className="text-brand mt-0.5 shrink-0" />
        <p className="text-sm text-onSurfaceSecondary"><b className="text-onSurface">Bonus notifiche:</b> attiva le notifiche e ricevi <b>+1 Readoo ogni giorno</b> (da convalidare) finché restano attive. Le attivi dal tuo Profilo o dal campanello in alto.</p>
      </div>

      {/* Streak */}
      <section className="mb-10">
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-onSurface mb-2"><Flame size={18} className="text-brand" /> Streak giornaliera</h2>
        <p className="text-sm text-subtle mb-4">
          La streak avanza leggendo <b>almeno 1 articolo valido al giorno</b>. Riparte da 1 se salti un giorno o dopo il 7° giorno.
          Streak attuale: <span className="font-semibold text-brand">{currentStreak} {currentStreak === 1 ? "giorno" : "giorni"}</span>.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {streakTable.map((s) => (
            <div key={s.day} data-testid={`streak-day-${s.day}`}
              className={`rounded-xl border p-3 text-center ${s.day === currentStreak ? "border-brand bg-brandTertiary" : "border-hair bg-surfaceSecondary"}`}>
              <p className="text-[11px] font-mono uppercase tracking-wider text-subtle">Giorno {s.day}</p>
              <p className="mt-1 inline-flex items-center gap-1 font-serif text-lg font-semibold text-onSurface"><Coins size={14} className="text-brand" /> +{s.coins}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Missioni giornaliere */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-onSurface"><Target size={18} className="text-brand" /> Missioni giornaliere</h2>
          {data && <span className="text-xs text-subtle">{data.totals.read} letti oggi · {data.totals.distinct} categorie</span>}
        </div>
        <p className="text-sm text-subtle mb-4">Ogni obiettivo si completa una sola volta al giorno e si azzera a mezzanotte.</p>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-brand" size={22} /></div>
        ) : (
          <div className="bg-surfaceSecondary border border-hair rounded-2xl p-4 sm:p-5 divide-y divide-hair/60">
            {daily.map((m) => <MissionRow key={m.id} m={m} />)}
            {allComplete && (
              <div data-testid="mission-all_complete" className="flex items-center gap-3 pt-3 mt-1">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${allComplete.done ? "bg-brand text-onBrand" : "bg-surfaceTertiary text-subtle"}`}>
                  {allComplete.done ? <Check size={16} /> : <Coins size={15} />}
                </span>
                <div className="flex items-center justify-between gap-3 flex-1">
                  <p className={`text-sm font-semibold ${allComplete.done ? "text-onSurface" : "text-onSurfaceSecondary"}`}>{allComplete.label}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-brand shrink-0"><Coins size={12} /> +{allComplete.reward}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
